import {
  GraphQLClient,
  type RequestMiddleware,
  type ResponseMiddleware,
  type Variables,
} from "graphql-request";
import { getDeviceId } from "~/api-client";
import type { DeliveryAddress } from "~/types";
import { wafFetch } from "~/utils/waf-token";
import {
  type AppScore,
  type CartItemInput,
  type CoordsInput,
  type FulfillmentType,
  type GetBaseMenusQuery,
  type GetMenusQuery,
  type GetPriorityMessagesQuery,
  type GetUpsellSuggestionsQuery,
  type GetWebLinksQuery,
  getSdk,
  type OrderAheadItem,
  ProfileScope,
  type SubmitUpsellSuggestionMutation,
  type UpsellCartInput,
} from "./generated";
import { headersToObject } from "./headers";

export { parseMutationErrorMessage } from "./errors";

interface ApiConfig {
  name?: string;
  apiUrl: string;
  appApiUrl: string;
  apiKey: string;
  appleWebClientId?: string;
}

export type FeatureKey =
  | "web.intelligent-upsells"
  | "client-jets.use-app-api"
  | "client-jets.app-api-ordering-queries"
  | "client-jets.app-api-app-review"
  | "web.deals-new";

export const FEATURE_KEYS: FeatureKey[] = [
  "web.intelligent-upsells",
  "client-jets.use-app-api",
  "client-jets.app-api-ordering-queries",
  "client-jets.app-api-app-review",
  "web.deals-new",
];

type AppApiFeatureKeys =
  | "client-jets.use-app-api"
  | "client-jets.app-api-ordering-queries"
  | "client-jets.app-api-app-review";

/** The `graphql-request` library uses this type internally, but doesn't export it */
export type RequestExtendedInit<V extends Variables = Variables> =
  RequestInit & {
    url: string;
    operationName?: string;
    variables?: V;
  };

export type GraphQlApiClient = ReturnType<typeof create>;

async function appApiFetchWrapper(path: string, apiKey?: string) {
  let headers = {};
  if (apiKey) headers = { headers: { "tidy-app-key": apiKey } };

  const resp = await wafFetch(path, headers);
  if (resp.status === 401) {
    // Only redirect on client-side, silently fail on server
    if (typeof window !== "undefined") {
      window.location.href = "/logout";
    }
    // On server, let the error propagate to be caught by error boundaries
  }

  const jsonBody = await resp.json();

  if (resp.status > 299 || resp.status < 200)
    throw new Error(jsonBody || "an unknown error occurred");

  return jsonBody;
}

export function create(
  config: ApiConfig,
  getToken: (req?: Request | RequestExtendedInit<Variables>) => string | null,
) {
  const requestMiddleware: RequestMiddleware = async (req) => {
    const token = getToken(req);

    return {
      ...req,
      headers: {
        ...headersToObject(req.headers),
        authorization: token ? `Bearer ${token}` : "",
        "Tidy-App-Key": config.apiKey,
      },
      url: req.operationName
        ? `${req.url}/${encodeURIComponent(req.operationName)}`
        : req.url,
    };
  };

  const responseMiddleware: ResponseMiddleware = (response) => {
    if (!(response instanceof Error) || !("response" in response)) return;

    // When there is a 401 response will be an error with .name "Error"
    // However it has a .response property available with has the status code
    const errResponse = (response as { response: { status: number } }).response;

    errResponse.status === 401 &&
      typeof window !== "undefined" &&
      (window.location.href = "/logout");
  };

  const graphql = getSdk(
    new GraphQLClient(`${config.apiUrl}/graphql`, {
      requestMiddleware,
      fetch: wafFetch,
      responseMiddleware,
    }),
  );

  // Public reads must NOT carry the auth token: on a shared origin a stale token for another merchant bleeds in and mis-scopes the query. App key only; never redirect to /logout.
  const anonRequestMiddleware: RequestMiddleware = async (req) => {
    return {
      ...req,
      headers: {
        ...headersToObject(req.headers),
        "Tidy-App-Key": config.apiKey,
      },
      url: req.operationName
        ? `${req.url}/${encodeURIComponent(req.operationName)}`
        : req.url,
    };
  };

  const anonGraphql = getSdk(
    new GraphQLClient(`${config.apiUrl}/graphql`, {
      requestMiddleware: anonRequestMiddleware,
      fetch: wafFetch,
    }),
  );

  const appApiRequestMiddleware: RequestMiddleware = async (req) => {
    const token = getToken(req);

    return {
      ...req,
      headers: {
        ...headersToObject(req.headers),
        // Sent even when empty, same as the legacy client: app-api only lets a
        // JWT cookie stand in for auth while the header is still present, so
        // clearing it is how logout takes effect immediately.
        authorization: token ? `Bearer ${token}` : "",
      },
      url: req.operationName
        ? `${req.url}/${encodeURIComponent(req.operationName)}`
        : req.url,
    };
  };

  const appApiGraphql = getSdk(
    new GraphQLClient(`${config.appApiUrl}/graphql`, {
      requestMiddleware: appApiRequestMiddleware,
      fetch: wafFetch,
      responseMiddleware,
    }),
  );

  // Anonymous app-api client (see anonGraphql) — the app-api host fixes the merchant, so public reads need no token.
  const anonAppApiRequestMiddleware: RequestMiddleware = async (req) => {
    return {
      ...req,
      url: req.operationName
        ? `${req.url}/${encodeURIComponent(req.operationName)}`
        : req.url,
    };
  };

  const anonAppApiGraphql = getSdk(
    new GraphQLClient(`${config.appApiUrl}/graphql`, {
      requestMiddleware: anonAppApiRequestMiddleware,
      fetch: wafFetch,
    }),
  );

  const appApisEnabled: Partial<Record<AppApiFeatureKeys, Promise<boolean>>> =
    {};

  const appApiEnabledPromise = async (
    key: AppApiFeatureKeys,
  ): Promise<boolean> => {
    if (appApisEnabled[key] !== undefined) return appApisEnabled[key];

    // Prevent multiple graphql calls by caching the promise
    appApisEnabled[key] = (async () => {
      try {
        const result = await graphql.GetFeatures({ keys: [key] });
        const feature = result.features.find((f) => f.key === key);
        return feature?.enabled ?? false;
      } catch {
        delete appApisEnabled[key];
        return false;
      }
    })();
    return appApisEnabled[key];
  };

  // Queries
  async function getActivity(after?: string) {
    return graphql.GetActivity({ after });
  }

  async function getActivitySummary() {
    return graphql.GetActivitySummary();
  }

  async function getAddresses(
    query: string,
    sessionToken: string,
    locationRef?: string,
  ) {
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.GetAddresses({ query, locationRef, sessionToken });
    }
    return graphql.GetAddresses({ query, locationRef, sessionToken });
  }

  async function getAddressDetails(ref: string, sessionToken: string) {
    return graphql.GetAddressDetails({ ref, sessionToken });
  }

  async function getFindPlace(query: string) {
    return appApiGraphql.FindPlace({ query });
  }

  async function getFeatures(location?: string) {
    let deviceId = "";
    if (typeof window !== "undefined") {
      deviceId = getDeviceId();
    }

    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.GetFeatures({
        keys: FEATURE_KEYS,
        deviceId,
        location,
      });
    }

    return graphql.GetFeatures({ keys: FEATURE_KEYS, deviceId, location });
  }

  async function getCredit(variables: { location: string }) {
    return graphql.GetCredit(variables);
  }

  async function getLocations() {
    // Public store list — sent anonymously (see anonAppApiGraphql).
    return anonAppApiGraphql.GetLocations({});
  }

  async function getLocationOrderingEnabled(locationRef: string) {
    // Public store data — sent anonymously (see anonGraphql) so a stale token can't mis-scope it.
    return anonGraphql.GetLocationOrderingEnabled({ locationRef });
  }

  async function getLanguageStrings() {
    return appApiGraphql.GetLanguageStrings();
  }

  async function getMerchant() {
    return appApiGraphql.GetMerchant();
  }

  async function getStripe() {
    return appApiGraphql.GetStripe();
  }

  async function getLoginConfig() {
    return graphql.GetLoginConfig();
  }

  async function getProfileFields() {
    return graphql.GetUserProfileFields({ scope: ProfileScope.Create });
  }

  async function getCurrentUser() {
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.GetCurrentUser();
    }
    return graphql.GetCurrentUser();
  }

  async function getPaymentMethods() {
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.GetPaymentMethods();
    }
    return graphql.GetPaymentMethods();
  }

  async function getOrdering(location: string) {
    // Public store ordering config — sent anonymously on both routes (see anonGraphql).
    if (await appApiEnabledPromise("client-jets.app-api-ordering-queries")) {
      return anonAppApiGraphql.GetOrdering({ location });
    }
    return anonGraphql.GetOrdering({
      location,
    });
  }

  async function getDynamicDelivery(location: string, to?: CoordsInput) {
    if (await appApiEnabledPromise("client-jets.app-api-ordering-queries")) {
      return appApiGraphql.GetDynamicDelivery({ location, to });
    }
    return graphql.GetDynamicDelivery({
      location,
      to,
    });
  }

  async function getMenus(
    location: string,
    dispatchOption: FulfillmentType | null,
  ): Promise<GetMenusQuery> {
    if (!dispatchOption) return { ordering: null };
    return await appApiFetchWrapper(
      `${config.apiUrl}/menu/${location}/${dispatchOption}`,
      config.apiKey,
    );
  }

  async function getBaseMenus(
    location: string,
    dispatchOption: FulfillmentType | null,
  ): Promise<GetBaseMenusQuery> {
    if (!dispatchOption) return { ordering: null };
    return await appApiFetchWrapper(
      `${config.apiUrl}/base-menu/${location}/${dispatchOption}`,
      config.apiKey,
    );
  }

  async function getPromotions(locationRef: string) {
    return graphql.GetPromotions({ locationRef });
  }

  async function getPromotionsHeader(locationRef: string) {
    return graphql.GetPromotionsHeader({ locationRef });
  }

  async function getRewards(endCursor: string) {
    return graphql.GetRewards({ after: endCursor });
  }

  async function getReward(id: string) {
    return graphql.GetReward({ id });
  }

  async function getPriorityMessages(locationRef: string | null) {
    if (!locationRef) {
      const result: GetPriorityMessagesQuery = { priorityMessages: [] };
      return result;
    }

    // this has to use app-api
    return appApiGraphql.GetPriorityMessages({ locationRef });
  }

  async function getWebLinks(locationRef: string | null) {
    if (!locationRef) {
      const result: GetWebLinksQuery = { webLinks: [] };
      return result;
    }

    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.GetWebLinks({ locationRef });
    }

    return graphql.GetWebLinks({ locationRef });
  }

  async function checkDeliveryAddress(
    locationRef: string,
    address: DeliveryAddress,
  ) {
    return graphql.CheckDeliveryAddress({ location: locationRef, address });
  }

  async function getRecentCarts(locationRef: string, limit: number) {
    if (await appApiEnabledPromise("client-jets.app-api-ordering-queries")) {
      return appApiGraphql.GetRecentCarts({ location: locationRef, limit });
    }
    return graphql.GetRecentCarts({ location: locationRef, limit });
  }

  async function getPromptAppReview() {
    if (await appApiEnabledPromise("client-jets.app-api-app-review")) {
      return appApiGraphql.GetPromptAppReview();
    }
    return graphql.GetPromptAppReview();
  }

  async function getLocationDeals(locationRef: string) {
    return appApiGraphql.GetLocationDeals({ locationRef });
  }

  // Mutations

  async function createOrder(variables: {
    id: string;
    locationRef: string;
    total: number;
    items: Array<OrderAheadItem> | OrderAheadItem;
    notes?: string;
    deliveryAddress?: DeliveryAddress;
    serviceRef?: string;
    tip?: number;
    timeSlot?: string;
  }) {
    return graphql.CreateOrder(variables);
  }

  async function createCart(variables: {
    id: string;
    locationRef: string;
    fulfillmentType: FulfillmentType;
    items: CartItemInput | CartItemInput[];
    tags: string[];
    notes?: string;
    deliveryAddress?: DeliveryAddress;
    serviceRef?: string;
    timeSlot?: string;
  }) {
    return graphql.CreateCart(variables);
  }

  async function createOrderFromCart(variables: {
    cartId: string;
    locationRef: string;
  }) {
    return graphql.CreateOrderFromCart(variables);
  }

  async function completeOrder(orderId: string, externalPayment?: boolean) {
    return graphql.CompleteOrder({ orderId, externalPayment });
  }

  async function updateOrderTip(orderId: string, tip: number) {
    return graphql.UpdateOrderTip({ orderId, tip });
  }

  async function cancelOrder(orderId: string) {
    return graphql.CancelOrder({ orderId });
  }

  async function requestPhoneVerification(phoneNumber: string) {
    return graphql.RequestPhoneVerification({ phoneNumber });
  }

  async function verifyPhone(code: string) {
    return graphql.VerifyPhone({ code });
  }

  async function claimPromotion(promotionId: string, locationRef: string) {
    return graphql.ClaimPromotion({ promotionId, locationRef });
  }

  async function startPromotionRedemption(
    promotionId: string,
    locationRef: string,
  ) {
    return graphql.StartPromotionRedemption({
      promotionId,
      locationRef,
    });
  }

  async function completePromotionRedemption(
    orderId: string,
    locationRef: string,
  ) {
    return graphql.CompletePromotionRedemption({
      orderId,
      locationRef,
    });
  }

  async function cancelPromotionRedemption(orderId: string) {
    return graphql.CancelPromotionRedemption({ orderId });
  }

  async function redeemCode(code: string) {
    return graphql.RedeemCode({ code });
  }

  async function removePaymentMethod(paymentMethod: string) {
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.RemovePaymentMethod({ paymentMethod });
    }
    return graphql.RemovePaymentMethod({ paymentMethod });
  }

  async function getMerchantRequiresPhone() {
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.GetMerchantRequiresPhone();
    }
    return graphql.GetMerchantRequiresPhone();
  }

  async function getPayRequirements() {
    return graphql.GetPayRequirements();
  }

  async function getPayment(paymentId: string) {
    return graphql.GetPayment({ paymentId });
  }

  async function getTransaction(paymentId: string) {
    return graphql.GetTransaction({ paymentId });
  }

  async function submitUpsellSuggestion(variables: {
    location: string;
    cart: UpsellCartInput;
    fulfillmentType: FulfillmentType;
  }): Promise<SubmitUpsellSuggestionMutation> {
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.SubmitUpsellSuggestion(variables);
    }
    return graphql.SubmitUpsellSuggestion(variables);
  }

  async function getUpsellSuggestions(
    requestId: string,
  ): Promise<GetUpsellSuggestionsQuery> {
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.GetUpsellSuggestions({ requestId });
    }
    return graphql.GetUpsellSuggestions({ requestId });
  }

  async function submitAppReview(score: AppScore) {
    let deviceId = "";
    if (process.browser) {
      deviceId = getDeviceId();
    }
    if (await appApiEnabledPromise("client-jets.use-app-api")) {
      return appApiGraphql.SubmitAppReview({ score, deviceId });
    }
    return graphql.SubmitAppReview({
      score,
      deviceId,
    });
  }

  async function createSetupIntent() {
    return graphql.CreateSetupIntent();
  }

  return {
    createCart,
    cancelOrder,
    createOrderFromCart,
    createOrder,
    completeOrder,
    updateOrderTip,
    getActivity,
    getActivitySummary,
    getAddresses,
    getAddressDetails,
    getFindPlace,
    getCredit,
    getCurrentUser,
    getPaymentMethods,
    getFeatures,
    getLanguageStrings,
    getLocations,
    getLocationOrderingEnabled,
    getMerchant,
    getStripe,
    getLoginConfig,
    getOrdering,
    getMenus,
    getPayment,
    getProfileFields,
    getPromotions,
    getPromotionsHeader,
    requestPhoneVerification,
    verifyPhone,
    claimPromotion,
    startPromotionRedemption,
    completePromotionRedemption,
    cancelPromotionRedemption,
    redeemCode,
    getMerchantRequiresPhone,
    getRewards,
    getReward,
    getWebLinks,
    getPriorityMessages,
    checkDeliveryAddress,
    removePaymentMethod,
    getPayRequirements,
    getRecentCarts,
    getPromptAppReview,
    submitAppReview,
    getTransaction,
    getBaseMenus,
    getDynamicDelivery,
    submitUpsellSuggestion,
    getUpsellSuggestions,
    createSetupIntent,
    getLocationDeals,
  };
}

// This copied from the generated code
export interface GqlLocation {
  __typename?: "Location";
  id: string;
  slug?: string | null;
  name: string;
  phoneNumber?: string | null;
  openingHours?: string | null;
  website?: string | null;
  orderAheadEnabled: boolean;
  orderAtTableEnabled: boolean;
  orderDeliveryEnabled: boolean;
  address?: {
    __typename?: "Address";
    street?: string | null;
    locality?: string | null;
    region?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null;
  coords?: {
    __typename?: "Coords";
    latitude: number;
    longitude: number;
  } | null;
  social?: {
    __typename?: "LocationSocialLinks";
    facebook?: string | null;
    twitter?: string | null;
    instagram?: string | null;
  } | null;
  timezone: string;
}
